
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface StripeConfig {
  priceId: string;
  webhookSecret: string;
  stripeSecretKey: string;
  publishableKey: string;
}

interface VercelConfig {
  projectId: string;
  teamId: string;
  accessToken: string;
}

const SuperAdminDashboard = () => {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const stripeForm = useForm<StripeConfig>();
  const vercelForm = useForm<VercelConfig>();

  // Load existing configurations
  const { data: stripeConfig, isLoading: isLoadingStripeConfig } = useQuery({
    queryKey: ["stripe-config"],
    queryFn: async () => {
      const configDoc = await getDoc(doc(db, "config", "stripe"));
      if (!configDoc.exists()) return null;
      const data = configDoc.data() as StripeConfig;
      stripeForm.reset(data);
      return data;
    },
  });

  const { data: vercelConfig, isLoading: isLoadingVercelConfig } = useQuery({
    queryKey: ["vercel-config"],
    queryFn: async () => {
      const configDoc = await getDoc(doc(db, "config", "vercel"));
      if (!configDoc.exists()) return null;
      const data = configDoc.data() as VercelConfig;
      vercelForm.reset(data);
      return data;
    },
  });

  const { data: funnels, isLoading: isLoadingFunnels } = useQuery({
    queryKey: ["admin-funnels"],
    queryFn: async () => {
      const querySnapshot = await getDocs(collection(db, "funnels"));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }
  });

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }
  });

  const onSubmitStripe = async (data: StripeConfig) => {
    setIsConfiguring(true);
    try {
      await setDoc(doc(db, "config", "stripe"), data);
      toast.success("Stripe configuration updated successfully");
    } catch (error) {
      console.error('Error updating stripe config:', error);
      toast.error("Failed to update Stripe configuration");
    } finally {
      setIsConfiguring(false);
    }
  };

  const onSubmitVercel = async (data: VercelConfig) => {
    setIsConfiguring(true);
    try {
      await setDoc(doc(db, "config", "vercel"), data);
      toast.success("Vercel configuration updated successfully");
    } catch (error) {
      console.error('Error updating vercel config:', error);
      toast.error("Failed to update Vercel configuration");
    } finally {
      setIsConfiguring(false);
    }
  };

  if (isLoadingFunnels || isLoadingUsers || isLoadingStripeConfig || isLoadingVercelConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <h1 className="text-3xl font-bold mb-8">Super Admin Dashboard</h1>
      
      <div className="grid gap-6 md:grid-cols-2 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Total Funnels</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{funnels?.length || 0}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{users?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Platform Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="stripe">
            <TabsList className="mb-4">
              <TabsTrigger value="stripe">Stripe Settings</TabsTrigger>
              <TabsTrigger value="vercel">Vercel Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="stripe">
              <Form {...stripeForm}>
                <form onSubmit={stripeForm.handleSubmit(onSubmitStripe)} className="space-y-4">
                  <FormField
                    control={stripeForm.control}
                    name="priceId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stripe Price ID</FormLabel>
                        <FormControl>
                          <Input placeholder="price_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={stripeForm.control}
                    name="webhookSecret"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Webhook Secret</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="whsec_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={stripeForm.control}
                    name="stripeSecretKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secret Key</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="sk_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={stripeForm.control}
                    name="publishableKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Publishable Key</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="pk_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={isConfiguring}>
                    {isConfiguring ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Stripe Configuration'
                    )}
                  </Button>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="vercel">
              <Form {...vercelForm}>
                <form onSubmit={vercelForm.handleSubmit(onSubmitVercel)} className="space-y-4">
                  <FormField
                    control={vercelForm.control}
                    name="projectId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project ID</FormLabel>
                        <FormControl>
                          <Input placeholder="prj_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={vercelForm.control}
                    name="teamId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Team ID</FormLabel>
                        <FormControl>
                          <Input placeholder="team_..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={vercelForm.control}
                    name="accessToken"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Access Token</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Enter Vercel access token..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={isConfiguring}>
                    {isConfiguring ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Vercel Configuration'
                    )}
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Funnels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {funnels?.slice(0, 5).map((funnel: any) => (
              <div key={funnel.id} className="p-4 border rounded-lg">
                <p className="font-medium">{funnel.name || "Unnamed Funnel"}</p>
                <p className="text-sm text-gray-500">ID: {funnel.id}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
